import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Mail, Check, ScanLine, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useState } from "react";
import DigestSettings from "@/components/DigestSettings";

function fmtWhen(iso) {
  if (!iso) return "never";
  const diff = (new Date() - new Date(iso)) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export async function startGmailOAuth() {
  try {
    const { data } = await api.get("/gmail/connect");
    if (!data?.auth_url) { toast.error("Could not start Gmail OAuth"); return; }
    window.location.href = data.auth_url;
  } catch (e) {
    const detail = e?.response?.data?.detail || "";
    if (e?.response?.status === 400 && /credentials/i.test(detail)) {
      toast.info("Gmail credentials not yet configured on this server — running in demo mode.");
    } else if (e?.response?.status === 401) {
      toast.error("Please sign in again");
    } else {
      toast.error(detail || "Could not start Gmail OAuth");
    }
  }
}

function statusSubtext(status, isLive) {
  if (!status.connected) {
    return status.credentials_available
      ? "Read-only scan. We never see money. Takes 5 seconds."
      : "Demo mode: sample leaks shown. Wire OAuth to scan real inbox.";
  }
  if (isLive) return `${status.live_leaks} real leaks · last scan ${fmtWhen(status.last_scan_at)}`;
  return `Read-only. Last scan ${fmtWhen(status.last_scan_at)}.`;
}

function GmailStatusHeader({ status }) {
  const isLive = status.connected && status.live_leaks > 0;
  return (
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="font-mono text-[11px] uppercase tracking-widest text-[#00E676] flex items-center gap-1.5">
          {isLive && <span className="w-1.5 h-1.5 rounded-full bg-[#00E676] live-dot" />}
          {isLive ? "LIVE · GMAIL SCAN" : "EMAIL SCAN"}
        </div>
        <h3 className="font-display text-lg font-bold tracking-tight mt-0.5">{status.connected ? "Gmail connected" : "Connect Gmail"}</h3>
        <p className="text-xs text-[#A0A7B0] mt-1" data-testid="gmail-connect-subtext">{statusSubtext(status, isLive)}</p>
      </div>
      {status.connected ? (
        <div className="w-9 h-9 rounded-md bg-[#00E676]/15 border border-[#00E676]/30 grid place-items-center shrink-0"><Check className="h-4 w-4 text-[#00E676]" /></div>
      ) : (
        <div className="w-9 h-9 rounded-md bg-[#1C1F22] border border-[#282C30] grid place-items-center shrink-0"><Mail className="h-4 w-4 text-[#A0A7B0]" /></div>
      )}
    </div>
  );
}

function ScanButton({ onScan }) {
  const [scanning, setScanning] = useState(false);
  const scan = async () => {
    setScanning(true);
    const t = toast.loading("Scanning your inbox for receipts…");
    try { await onScan(); toast.dismiss(t); } finally { setScanning(false); }
  };
  return (
    <Button className="btn-emerald flex-1 h-9" onClick={scan} disabled={scanning} data-testid="gmail-scan-button">
      {scanning ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Scanning…</> : <><ScanLine className="h-4 w-4 mr-1" /> Rescan now</>}
    </Button>
  );
}

export default function GmailConnect({ status, onScan, onChange }) {
  const needsReconnect = status.connected && !status.can_send;
  return (
    <Card className="tactile-card rounded-md p-5" data-testid="gmail-connect-card">
      <GmailStatusHeader status={status} />
      <div className="mt-4 flex gap-2">
        {status.connected ? (
          <ScanButton onScan={onScan} />
        ) : (
          <Button className="btn-emerald flex-1 h-9" onClick={startGmailOAuth} data-testid="gmail-connect-button">
            <Mail className="h-4 w-4 mr-1" /> {status.credentials_available ? "Connect Gmail" : "Setup Gmail (Admin)"}
          </Button>
        )}
      </div>
      {needsReconnect && (
        <button onClick={startGmailOAuth} className="mt-2 w-full text-left text-xs text-[#FFC857] hover:underline" data-testid="gmail-reconnect-button">
          Reconnect Gmail to enable emailed briefs →
        </button>
      )}
      <DigestSettings status={status} onChange={onChange} />
    </Card>
  );
}
